import React, { FC, useState } from 'react';
import { MainFrame } from '../components/main-frame';
import {
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Texts } from '../../../shared/types';
import { SIGN_IN_ROUTES } from '../sign-in/route.definitions';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useHttpMutation } from '../../../api';
import { EMAIL_PATTERN } from '../../../shared/consts';
import { BhabiLink, PasswordInput, useAlert } from '../../../shared/components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { withNext } from '../components/auth-links';

export interface SignUpPageProps {
  texts?: Texts<'signUpTitle' | 'button'>;
}

export const SignUpPage: FC<SignUpPageProps> = ({
  texts = {
    signUpTitle: 'Create your account',
    button: 'Create account',
  },
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      email: '',
      password: '',
    },
  });

  const { mutate: signUp, isLoading } = useSignUp();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next');

  const { addAlert } = useAlert();

  const navigate = useNavigate();

  const onSubmit: SubmitHandler<Omit<SignUpInput, 'firebaseUID'>> = data => {
    signUp(
      { ...data, email: data.email.trim().toLowerCase() },
      {
        onError: err => {
          addAlert({
            type: 'error',
            message: err.response?.data?.message || 'Failed to Sign Up',
          });
        },
        onSuccess: () => {
          addAlert({
            type: 'success',
            message: 'Signed up successfully, please login.',
          });

          navigate(withNext(SIGN_IN_ROUTES.ABSOLUTE_PATH, next));
        },
      }
    );
  };

  const [acceptedToc, setAcceptedToc] = useState(false);

  return (
    <MainFrame
      eyebrow="Pull up a chair"
      title={texts.signUpTitle}
      documentTitle="Create account"
      subtitle="An account keeps your coins, friends and invites on every device."
      stackProps={{ onSubmit: handleSubmit(onSubmit) }}
      linkPath={withNext(SIGN_IN_ROUTES.ABSOLUTE_PATH, next)}
      texts={{
        linkMessage: 'Already have an account? Sign in',
        forgotPassword: '',
      }}
    >
      <TextField
        {...register('name', {
          required: `Name is required.`,
        })}
        label="Name"
        autoComplete="name"
        error={!!errors.name}
        helperText={errors.name?.message || undefined}
        fullWidth
      />
      <TextField
        {...register('email', {
          required: `Email is required.`,
          pattern: EMAIL_PATTERN,
        })}
        label="Email"
        type="email"
        autoComplete="email"
        inputMode="email"
        error={!!errors.email}
        helperText={errors.email?.message || undefined}
        fullWidth
      />
      <PasswordInput<SignUpInput>
        controllerProps={{
          control,
          name: 'password',
          defaultValue: '',
          rules: {
            required: `Password is required.`,
            validate: value =>
              (value && value.length >= 8) || `Password is too short.`,
          },
        }}
        textFieldProps={{
          label: 'Password',
          autoComplete: 'new-password',
          error: !!errors.password,
          helperText: errors.password?.message || 'At least 8 characters.',
          fullWidth: true,
        }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={acceptedToc}
            onChange={e => setAcceptedToc(e.target.checked)}
          />
        }
        label={<AcceptTermsOfServiceMsg />}
        sx={{
          alignItems: 'flex-start',
          mx: 0,
          '& .MuiCheckbox-root': { mt: -0.75 },
        }}
      />
      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={!acceptedToc || isLoading}
        fullWidth
      >
        {isLoading ? 'Creating account…' : texts.button}
      </Button>
    </MainFrame>
  );
};

const AcceptTermsOfServiceMsg: FC = () => (
  <Typography variant="body2" color="textSecondary" sx={{ pt: 0.25 }}>
    {`By signing up for a Bhabi card game account, you agree to the `}
    <BhabiLink to="/toc">Terms of Service and Privacy Policy</BhabiLink>
  </Typography>
);

export type SignUpInput = {
  name: string;
  email: string;
  password: string;
  firebaseUID?: string;
};

function useSignUp() {
  return useHttpMutation<{ message: string }, SignUpInput>('/sign-up', 'POST');
}
